import { useEffect, useState } from "react";
import { CheckCircle2, Chrome, Download, KeyRound, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deleteCookie, getCookieStatus, saveCookie } from "@/lib/settings";
import { useExtensionStatus } from "@/lib/extension";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function ExtensionPanel({ extension }) {
  const installed = extension.installed;
  return (
    <div
      className={`border px-4 py-4 ${
        installed ? "border-emerald-400/30 bg-emerald-400/[0.04]" : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid="extension-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Chrome className={`w-4 h-4 ${installed ? "text-emerald-400" : "text-gold"}`} />
        <span className="text-sm font-medium text-white">
          Estensione Chrome — <span className={installed ? "text-emerald-400" : "text-white/55"}>
            {installed ? `installata (v${extension.version})` : "non rilevata"}
          </span>
        </span>
      </div>

      {!installed ? (
        <div className="text-xs text-white/55 leading-relaxed space-y-3">
          <p>
            L'estensione gira nel <strong className="text-white">tuo browser</strong>,
            quindi le chiamate a Catawiki passano dalla TUA sessione (con PerimeterX OK) —
            niente cookie da copiare, niente blocchi anti-bot.
          </p>
          <ol className="list-decimal list-inside space-y-1.5 pl-1">
            <li>
              <a
                href={`${API}/extension/download`}
                className="text-gold underline decoration-gold underline-offset-2 hover:text-gold-hover inline-flex items-center gap-1"
                data-testid="extension-download"
              >
                <Download className="w-3 h-3" />
                Scarica <code>catawiki-bridge.zip</code>
              </a>{" "}
              ed estraila in una cartella sul tuo PC.
            </li>
            <li>Apri Chrome → <code className="text-gold">chrome://extensions</code></li>
            <li>Attiva <strong className="text-white">Modalità sviluppatore</strong> (toggle in alto a destra).</li>
            <li>Clicca <strong className="text-white">"Carica estensione non pacchettizzata"</strong> e seleziona la cartella scompattata.</li>
            <li>Assicurati di essere <strong className="text-white">loggato su catawiki.com</strong> nello stesso browser.</li>
            <li><strong className="text-white">Ricarica questa pagina</strong> — l'estensione verrà rilevata automaticamente.</li>
          </ol>
        </div>
      ) : (
        <p className="text-xs text-white/55 leading-relaxed">
          Tutto pronto. Le card della ricerca verranno arricchite automaticamente con
          costi spedizione in EUR, città di ritiro e disponibilità — usando la tua
          sessione Catawiki locale.
        </p>
      )}
    </div>
  );
}

function CookiePanel({ status, onRefresh }) {
  const [cookieValue, setCookieValue] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    if (!cookieValue.trim()) return;
    setBusy(true);
    try {
      await saveCookie(cookieValue.trim());
      setCookieValue("");
      toast.success("Cookie salvato. Lo userò come fallback durante le ricerche.");
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvare il cookie.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteCookie();
      toast.success("Cookie rimosso.");
      await onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="border border-white/10 px-4 py-3 bg-white/[0.02]" data-testid="cookie-panel">
      <summary className="cursor-pointer text-xs uppercase tracking-[0.2em] text-white/55 select-none">
        Fallback: cookie server-side (avanzato)
      </summary>
      <div className="mt-4 space-y-3 text-xs text-white/55">
        <p className="leading-relaxed">
          Metodo legacy: salva il cookie sul backend e tenta di chiamare Catawiki dal server.
          PerimeterX spesso lo blocca — usa l'estensione sopra se possibile.
        </p>
        {status.has_cookie ? (
          <div className="flex items-center justify-between border border-white/10 px-3 py-2">
            <span className="flex items-center gap-2 text-white/70">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              Cookie attivo · <span className="font-mono">{status.masked}</span>
            </span>
            <button type="button" onClick={handleDelete} disabled={busy} className="text-red-400 hover:text-red-300 text-xs" data-testid="cookie-delete">
              <Trash2 className="w-3 h-3 inline mr-1" />Rimuovi
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-white/45 border border-white/5 px-3 py-2" data-testid="cookie-status-empty">
            <X className="w-3.5 h-3.5" /> Nessun cookie salvato.
          </div>
        )}
        <textarea
          value={cookieValue}
          onChange={(e) => setCookieValue(e.target.value)}
          placeholder="oauth_token=eyJ... (o l'intero header Cookie)"
          rows={3}
          className="w-full bg-transparent border border-white/15 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-gold transition-colors"
          data-testid="cookie-input"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={busy || !cookieValue.trim()}
            className="rounded-none bg-white/10 text-white hover:bg-gold hover:text-black transition-all duration-300 px-5 text-[10px] uppercase tracking-[0.22em] font-medium disabled:opacity-50"
            data-testid="cookie-save"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Salva cookie"}
          </Button>
        </div>
      </div>
    </details>
  );
}

export default function SettingsDialog({ open, onOpenChange, onStatusChange }) {
  const extension = useExtensionStatus();
  const [cookieStatus, setCookieStatus] = useState({ has_cookie: false });

  const refreshStatus = async () => {
    const s = await getCookieStatus();
    setCookieStatus(s);
    onStatusChange?.(s);
  };

  useEffect(() => {
    if (open) refreshStatus();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-foreground max-w-2xl" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-gold" />
            Sessione Catawiki
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-white/55 leading-relaxed">
            Per vedere i costi esatti di spedizione verso l'Italia e le info di ritiro, l'app
            ha bisogno di accedere a Catawiki autenticato. Il metodo consigliato è l'<strong className="text-white">estensione Chrome</strong> qui sotto.
          </p>

          <ExtensionPanel extension={extension} />

          <CookiePanel status={cookieStatus} onRefresh={refreshStatus} />

          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/60"
              data-testid="settings-close"
            >
              Chiudi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
